import {
  Checkbox,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid,
  IconButton,
  Paper,
  Typography,
} from '@material-ui/core';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import TextField from '@material-ui/core/TextField';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import WarningIcon from '@material-ui/icons/Warning';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { useFormik } from 'formik';
import { isEqual, isUndefined } from 'lodash-es';
import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { ENQUEUE_SUCCESS_CONFIG } from 'src/consts/ENQUEUE_CONFIG';
import { Transition, useStyles } from 'src/consts/styles';
import useMutateCreateRider from 'src/hooks/MutateCreateRider';
import useMutateDeleteRider from 'src/hooks/MutateDeleteRider';
import useFirestoreUpload from 'src/hooks/useFirestoreUpload';
import useQueryListAllRestaurants from 'src/hooks/useQueryListAllRestaurants';
import useQueryListAllUsers from 'src/hooks/useQueryListAllUsers';
import useUpdateStadiumInfo from 'src/hooks/useUpdateStadiumInfo';
import active_lang from 'src/langs/jp_en';
import isBase64Image from 'src/utils/isBase64Image';
import isHttpLink from 'src/utils/isHttpLink';
import * as Yup from 'yup';
import ImageHandler from './ImageHandler';

export default function ConfirmEditStadiumDialog({
  open,
  setOpen,
  stadium_id,
  initial_data,
  refetch,
}) {
  let classes = useStyles();
  let mutateFirestoreUpload = useFirestoreUpload();
  let mutationUpdateStadiumInfo = useUpdateStadiumInfo();
  const [user_options, setUserOptions] = React.useState([]);
  const language_options = [
    { id: 'ja', name: 'Japanese' },
    { id: 'en', name: 'English' },
  ];
  const getLanguageOptionById = id =>
    language_options.find(option => option.id === id) || null;
  let mutationCreateRider = useMutateCreateRider();
  let mutationDeleteRider = useMutateDeleteRider();
  let list_all_user_result = useQueryListAllUsers();
  let query_list_all_restaurants = useQueryListAllRestaurants();

  const [options, setOptions] = useState(
    initial_data.zones
      ? initial_data.zones.map((zone, index) => ({ id: index + 1, ...zone }))
      : []
  );
  const [openModal, setOpenModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [editZoneName, setEditZoneName] = useState('');
  const [editMapValue, setEditMapValue] = useState('');

  useEffect(() => {
    formik.setFieldValue(
      'zones',
      options.map(({ zoneName, mapValue }) => ({ zoneName, mapValue }))
    );
  }, [options]);

  const handleOpenModal = (type, id = null) => {
    setModalType(type);
    setSelectedId(id);
    if (type === 'edit') {
      const selectedOption = options.find(opt => opt.id === id);
      setEditZoneName(selectedOption?.zoneName || '');
      setEditMapValue(selectedOption?.mapValue || '');
    }
    if (type === 'add') {
      setEditZoneName('');
      setEditMapValue('');
    }
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const addOption = () => {
    handleOpenModal('add');
  };

  const confirmAddOption = () => {
    if (!editZoneName.trim() || !editMapValue.trim()) {
      enqueueSnackbar('Zone name and map value must not be empty', {
        variant: 'error',
        autoHideDuration: 1500,
      });
      return;
    }
    const newId = options.length + 1;
    const newZone = {
      id: newId,
      zoneName: editZoneName,
      mapValue: editMapValue,
    };
    const newOptions = [...options, newZone];
    setOptions(newOptions);
    handleCloseModal();
  };

  const confirmEditOption = () => {
    if (!editZoneName.trim() || !editMapValue.trim()) {
      enqueueSnackbar('Zone name and map value must not be empty', {
        variant: 'error',
      });
      return;
    }
    const newOptions = options.map(opt =>
      opt.id === selectedId
        ? { ...opt, zoneName: editZoneName, mapValue: editMapValue }
        : opt
    );
    setOptions(newOptions);
    handleCloseModal();
  };

  const deleteOption = id => {
    handleOpenModal('delete', id);
  };

  const confirmDeleteOption = () => {
    const newOptions = options.filter(opt => opt.id !== selectedId);
    setOptions(newOptions);
    handleCloseModal();
  };

  const restaurants = React.useMemo(() => {
    if (!query_list_all_restaurants.data) {
      return [];
    }

    return query_list_all_restaurants.data.map(restaurant => ({
      id: restaurant.id,
      name: restaurant.name,
    }));
  }, [query_list_all_restaurants.data]);

  React.useEffect(() => {
    if (
      isUndefined(list_all_user_result.data) ||
      isUndefined(list_all_user_result.data.users)
    ) {
      return;
    } else {
      const { users } = list_all_user_result.data;
      const option_list = users.map(entry => {
        return { uid: entry.uid, username: entry.email };
      });
      setUserOptions(option_list);
    }
  }, [list_all_user_result.data, open]);

  const { enqueueSnackbar } = useSnackbar();

  const handleClose = () => {
    formik.resetForm();
    setOpen(false);
  };

  const formik = useFormik({
    enableReinitialize: true,
    validationSchema: Yup.object({
      name: Yup.string().required(active_lang.STADIUM_NAME_IS_REQUIRED),
      promotion_restaurant_id: Yup.string().when('isStadiumPromotion', {
        is: true,
        then: Yup.string().required(active_lang.STADIUM_NAME_IS_REQUIRED),
        otherwise: Yup.string().notRequired(),
      }),
      zones: Yup.array()
        .of(
          Yup.object().shape({
            zoneName: Yup.string()
              .trim()
              .required('Zone name is required'),
            mapValue: Yup.string()
              .trim()
              .required('Map value is required'),
          })
        )
        .min(1, 'At least one zone is required'),
    }),
    initialValues: {
      ...initial_data,
      super_administrators: initial_data.super_administrators || [],
      cms_settings: {
        pre_order: initial_data.cms_settings?.pre_order || false,
      },
      zones: initial_data.zones || [],
    },
    onSubmit: async values => {
      if (!values.isStadiumPromotion) {
        values.promotion_restaurant_id = '';
      }

      if (values.zones.some(zone => !zone.zoneName || !zone.mapValue)) {
        enqueueSnackbar('All zones must have a name and map value', {
          variant: 'error',
          autoHideDuration: 1500,
        });
        return;
      }

      let file_path = `stadium_info/${stadium_id}/stadium_profile`;
      let image_base64 = values.stadium_profile_pic;
      const keepUsers = [
        ...(Array.isArray(values.riders) ? values.riders : []),
      ];

      await mutationDeleteRider.mutateAsync({
        stadium_id: stadium_id,
        riders: keepUsers,
      });

      if (Array.isArray(values.riders)) {
        values.riders.map(item => {
          return mutationCreateRider.mutateAsync({
            id: item.uid,
            payload: {
              stadium_id: stadium_id,
              is_online: true,
              role: 'RIDER',
              user_name: item.username,
            },
          });
        });
      }
      if (isBase64Image(image_base64)) {
        mutateFirestoreUpload
          .mutateAsync({
            file_path,
            file_base64: image_base64,
          })
          .then(data => {
            return mutationUpdateStadiumInfo.mutateAsync({
              rest_id: stadium_id,
              payload: { ...values, stadium_profile_pic: data.download_url },
            });
          })
          .then(() => {
            enqueueSnackbar(
              active_lang.RESTAURANT_CONFIG_UPDATE_SUCCESS,
              ENQUEUE_SUCCESS_CONFIG
            );
            refetch();
          })
          .then(() => {
            handleClose();
          })
          .catch(err => {
            console.error('err', err);
          });
      } else if (isHttpLink(values.stadium_profile_pic)) {
        delete values.image_urls;
        mutationUpdateStadiumInfo
          .mutateAsync({
            rest_id: stadium_id,
            payload: values,
          })
          .then(() => {
            enqueueSnackbar(
              active_lang.STADIUM_CONFIG_UPDATE_SUCCESS,
              ENQUEUE_SUCCESS_CONFIG
            );
            refetch();
          })
          .then(() => {
            handleClose();
          })
          .catch(err => {
            console.error('err', err);
          });
      } else {
        mutationUpdateStadiumInfo
          .mutateAsync({
            rest_id: stadium_id,
            payload: {
              ...values,
              stadium_profile_pic: '',
            },
          })
          .then(() => {
            enqueueSnackbar(
              active_lang.STADIUM_CONFIG_UPDATE_SUCCESS,
              ENQUEUE_SUCCESS_CONFIG
            );
            refetch();
          })
          .then(() => {
            handleClose();
          })
          .catch(err => {
            console.error('err', err);
          });
      }
    },
  });

  return (
    <>
      <Dialog
        open={open}
        TransitionComponent={Transition}
        keepMounted
        onClose={handleClose}
        aria-labelledby="alert-dialog-slide-title"
        aria-describedby="alert-dialog-slide-description"
        maxWidth="md"
        fullWidth
      >
        <form
          onSubmit={e => {
            e.preventDefault();
            formik.handleSubmit();
          }}
        >
          <DialogContent>
            <Box mt="1rem">
              <FormControl fullWidth>
                <TextField
                  id="name"
                  {...formik.getFieldProps('name')}
                  className={classes.TextInput}
                  InputProps={{ disableUnderline: true }}
                  inputProps={{ style: { padding: '1rem' } }}
                  label={active_lang.ADMIN_RESTAURANT_NAME}
                  variant="outlined"
                  fullWidth
                />
                {formik.touched.name && formik.errors.name ? (
                  <Box className={classes.field_error_highlight}>
                    <Box pr="1rem">
                      <WarningIcon fontSize="small" />
                    </Box>
                    <Box>{formik.errors.name}</Box>
                  </Box>
                ) : null}
              </FormControl>
            </Box>

            <Box mt={'1rem'}>
              <FormControl fullWidth>
                <ImageHandler
                  id="stadium_profile_pic"
                  {...formik.getFieldProps('stadium_profile_pic')}
                />
              </FormControl>
            </Box>

            <Box mt={'1rem'}>
              <Autocomplete
                id="administrators"
                {...formik.getFieldProps('administrators')}
                options={user_options}
                getOptionLabel={option => option.username}
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Administrators select"
                    variant="outlined"
                  />
                )}
                onChange={(event, newValue) =>
                  formik.setFieldValue('administrators', newValue)
                }
                disableClearable
                multiple
              />
            </Box>

            <Box mt={'1rem'}>
              <Autocomplete
                id="super_administrators"
                {...formik.getFieldProps('super_administrators')}
                options={user_options}
                getOptionLabel={option => option.username}
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Super Administrators select"
                    variant="outlined"
                  />
                )}
                onChange={(event, newValue) =>
                  formik.setFieldValue('super_administrators', newValue)
                }
                disableClearable
                multiple
              />
            </Box>

            <Box mt={'1rem'}>
              <Autocomplete
                id="riders"
                {...formik.getFieldProps('riders')}
                options={user_options}
                getOptionLabel={option => option.username}
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Rider select"
                    variant="outlined"
                  />
                )}
                onChange={(event, newValue) => {
                  formik.setFieldValue('riders', newValue);
                }}
                disableClearable
                multiple
              />
            </Box>

            <Box mt="1rem">
              <FormControl fullWidth>
                <TextField
                  id="address"
                  {...formik.getFieldProps('address')}
                  className={classes.TextInput}
                  InputProps={{ disableUnderline: true }}
                  inputProps={{ style: { padding: '1rem' } }}
                  label={active_lang.STADIUM_ADDRESS}
                  variant="outlined"
                  fullWidth
                />
                {formik.touched.address && formik.errors.address ? (
                  <Box className={classes.field_error_highlight}>
                    <Box pr="1rem">
                      <WarningIcon fontSize="small" />
                    </Box>
                    <Box>{formik.errors.address}</Box>
                  </Box>
                ) : null}
              </FormControl>
            </Box>

            <Box mt="1rem">
              <FormControl fullWidth>
                <TextField
                  id="business_name"
                  {...formik.getFieldProps('business_name')}
                  className={classes.TextInput}
                  InputProps={{ disableUnderline: true }}
                  inputProps={{ style: { padding: '1rem' } }}
                  label={active_lang.STADIUM_BUSINESS_NAME}
                  variant="outlined"
                  fullWidth
                />
                {formik.touched.business_name && formik.errors.business_name ? (
                  <Box className={classes.field_error_highlight}>
                    <Box pr="1rem">
                      <WarningIcon fontSize="small" />
                    </Box>
                    <Box>{formik.errors.business_name}</Box>
                  </Box>
                ) : null}
              </FormControl>
            </Box>

            <Box mt="1rem">
              <FormControl fullWidth>
                <TextField
                  id="registration_code"
                  {...formik.getFieldProps('registration_code')}
                  className={classes.TextInput}
                  InputProps={{ disableUnderline: true }}
                  inputProps={{ style: { padding: '1rem' } }}
                  label={active_lang.STADIUM_REGISTRATION_CODE}
                  variant="outlined"
                  fullWidth
                />
                {formik.touched.registration_code &&
                formik.errors.registration_code ? (
                  <Box className={classes.field_error_highlight}>
                    <Box pr="1rem">
                      <WarningIcon fontSize="small" />
                    </Box>
                    <Box>{formik.errors.registration_code}</Box>
                  </Box>
                ) : null}
              </FormControl>
            </Box>

            <Box mt="1rem">
              <FormControl fullWidth>
                <TextField
                  id="phone"
                  {...formik.getFieldProps('phone')}
                  className={classes.TextInput}
                  InputProps={{ disableUnderline: true }}
                  inputProps={{ style: { padding: '1rem' } }}
                  label={active_lang.STADIUM_PHONE}
                  variant="outlined"
                  fullWidth
                />
                {formik.touched.phone && formik.errors.phone ? (
                  <Box className={classes.field_error_highlight}>
                    <Box pr="1rem">
                      <WarningIcon fontSize="small" />
                    </Box>
                    <Box>{formik.errors.phone}</Box>
                  </Box>
                ) : null}
              </FormControl>
            </Box>

            <Box mt="1rem">
              <FormControl fullWidth>
                <TextField
                  id="rider_contact"
                  {...formik.getFieldProps('rider_contact')}
                  className={classes.TextInput}
                  InputProps={{ disableUnderline: true }}
                  inputProps={{ style: { padding: '1rem' } }}
                  label={active_lang.RIDER_CONTACT}
                  variant="outlined"
                  fullWidth
                />
                {formik.touched.rider_contact && formik.errors.rider_contact ? (
                  <Box className={classes.field_error_highlight}>
                    <Box pr="1rem">
                      <WarningIcon fontSize="small" />
                    </Box>
                    <Box>{formik.errors.rider_contact}</Box>
                  </Box>
                ) : null}
              </FormControl>
            </Box>

            <Box mt="1rem">
              <FormControl fullWidth>
                <TextField
                  id="service_name"
                  {...formik.getFieldProps('service_name')}
                  className={classes.TextInput}
                  InputProps={{ disableUnderline: true }}
                  inputProps={{ style: { padding: '1rem' } }}
                  label={active_lang.ADMIN_RESTAURANT_SERVICE_NAME}
                  variant="outlined"
                  fullWidth
                />
                {formik.touched.service_name && formik.errors.service_name ? (
                  <Box className={classes.field_error_highlight}>
                    <Box pr="1rem">
                      <WarningIcon fontSize="small" />
                    </Box>
                    <Box>{formik.errors.service_name}</Box>
                  </Box>
                ) : null}
              </FormControl>
            </Box>

            <Box mt="1rem">
              <FormControl fullWidth>
                <TextField
                  id="email_retention_days"
                  {...formik.getFieldProps('email_retention_days')}
                  className={classes.TextInput}
                  InputProps={{ disableUnderline: true }}
                  inputProps={{
                    style: { padding: '1rem' },
                    type: 'number',
                  }}
                  label={active_lang.EMAIL_RETENTION_DAYS}
                  variant="outlined"
                  fullWidth
                  value={formik.values.email_retention_days}
                  onChange={e =>
                    formik.setFieldValue(
                      'email_retention_days',
                      Number(e.target.value)
                    )
                  }
                />
                {formik.touched.email_retention_days &&
                formik.errors.email_retention_days ? (
                  <Box className={classes.field_error_highlight}>
                    <Box pr="1rem">
                      <WarningIcon fontSize="small" />
                    </Box>
                    <Box>{formik.errors.email_retention_days}</Box>
                  </Box>
                ) : null}
              </FormControl>
            </Box>

            <Box mt="1rem">
              <FormControl fullWidth>
                <TextField
                  id="max_banner_quantity"
                  {...formik.getFieldProps('max_banner_quantity')}
                  className={classes.TextInput}
                  InputProps={{ disableUnderline: true }}
                  inputProps={{
                    style: { padding: '1rem' },
                    type: 'number',
                  }}
                  label={active_lang.MAX_BANNER_QUANTITY}
                  variant="outlined"
                  fullWidth
                  value={formik.values.max_banner_quantity}
                  onChange={e =>
                    formik.setFieldValue(
                      'max_banner_quantity',
                      Number(e.target.value)
                    )
                  }
                />
                {formik.touched.max_banner_quantity &&
                formik.errors.max_banner_quantity ? (
                  <Box className={classes.field_error_highlight}>
                    <Box pr="1rem">
                      <WarningIcon fontSize="small" />
                    </Box>
                    <Box>{formik.errors.max_banner_quantity}</Box>
                  </Box>
                ) : null}
              </FormControl>
            </Box>

            <Box mt="1rem">
              <FormControl fullWidth>
                <TextField
                  id="max_recommend_quantity"
                  {...formik.getFieldProps('max_recommend_quantity')}
                  className={classes.TextInput}
                  InputProps={{ disableUnderline: true }}
                  inputProps={{
                    style: { padding: '1rem' },
                    type: 'number',
                  }}
                  label={active_lang.MAX_RECOMMEND_QUANTITY}
                  variant="outlined"
                  fullWidth
                  value={formik.values.max_recommend_quantity}
                  onChange={e =>
                    formik.setFieldValue(
                      'max_recommend_quantity',
                      Number(e.target.value)
                    )
                  }
                />
                {formik.touched.max_recommend_quantity &&
                formik.errors.max_recommend_quantity ? (
                  <Box className={classes.field_error_highlight}>
                    <Box pr="1rem">
                      <WarningIcon fontSize="small" />
                    </Box>
                    <Box>{formik.errors.max_recommend_quantity}</Box>
                  </Box>
                ) : null}
              </FormControl>
            </Box>

            <Grid item xs={12} lg={6}>
              <Box mt="1rem">
                <FormControl fullWidth>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="isStadiumPromotion"
                        checked={formik.values.isStadiumPromotion ?? false}
                        onChange={formik.handleChange}
                      />
                    }
                    label="ENABLE PROMOTION"
                  />
                </FormControl>
              </Box>
            </Grid>

            <Grid item xs={12} lg={6}>
              <Box mt="1rem">
                <FormControl fullWidth>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="enable_sales_count"
                        checked={formik.values.enable_sales_count ?? false}
                        onChange={formik.handleChange}
                      />
                    }
                    label={active_lang.ENABLE_SALES_COUNT}
                  />
                </FormControl>
              </Box>
            </Grid>

            <Grid item xs={12} lg={6}>
              <Box mt="1rem">
                <FormControl fullWidth>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="isSeeAll"
                        checked={formik.values.isSeeAll ?? false}
                        onChange={formik.handleChange}
                      />
                    }
                    label="ENABLE SEE ALL"
                  />
                </FormControl>
              </Box>
            </Grid>
            <Grid item xs={12} lg={6}>
              <Box mt="1rem">
                <FormControl fullWidth>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="enable_preorder"
                        checked={formik.values.cms_settings?.pre_order ?? false}
                        onChange={e =>
                          formik.setFieldValue(
                            'cms_settings.pre_order',
                            e.target.checked
                          )
                        }
                      />
                    }
                    label="ENABLE MENY GO V2"
                  />
                </FormControl>
              </Box>
            </Grid>
            <Grid item xs={12} lg={6}>
              <Box mt="1rem">
                <FormControl fullWidth>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="enable_terms"
                        checked={formik.values.enable_terms ?? false}
                        onChange={formik.handleChange}
                      />
                    }
                    label="ENABLE TERMS"
                  />
                </FormControl>
              </Box>
            </Grid>

            <Box mt="1rem">
              <FormControl fullWidth>
                <TextField
                  id="session_expire_hours"
                  {...formik.getFieldProps('session_expire_hours')}
                  className={classes.TextInput}
                  InputProps={{ disableUnderline: true }}
                  inputProps={{
                    style: { padding: '1rem' },
                    type: 'number',
                  }}
                  label={active_lang.SESSION_EXPIRY}
                  variant="outlined"
                  fullWidth
                  value={formik.values.session_expire_hours}
                  onChange={e =>
                    formik.setFieldValue(
                      'session_expire_hours',
                      Number(e.target.value)
                    )
                  }
                />
                {formik.touched.session_expire_hours &&
                formik.errors.session_expire_hours ? (
                  <Box className={classes.field_error_highlight}>
                    <Box pr="1rem">
                      <WarningIcon fontSize="small" />
                    </Box>
                    <Box>{formik.errors.session_expire_hours}</Box>
                  </Box>
                ) : null}
              </FormControl>
            </Box>

            <Grid item xs={12}>
              <Box mt="1rem">
                <FormControl component="fieldset">
                  <FormLabel component="legend">
                    {active_lang.ENABLE_MENU_QUANTITY_CONTROL}
                  </FormLabel>
                  <FormGroup
                    style={{
                      display: 'flex',
                      gap: '8px',
                    }}
                  >
                    <Box
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: '8px',
                        alignItems: 'center',
                        paddingTop: '8px',
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            id="enable_food_quantity_control"
                            checked={
                              isUndefined(formik.values) ||
                              isUndefined(
                                formik.values.enable_food_quantity_control
                              )
                                ? false
                                : formik.values.enable_food_quantity_control
                            }
                            {...formik.getFieldProps(
                              'enable_food_quantity_control'
                            )}
                            inputProps={{
                              'aria-label': active_lang.FOOD,
                            }}
                          />
                        }
                        label={active_lang.FOOD}
                      />
                      {formik.values.enable_food_quantity_control && (
                        <TextField
                          id="max_food_quantity"
                          {...formik.getFieldProps('max_food_quantity')}
                          className={classes.TextInput}
                          InputProps={{ disableUnderline: true }}
                          inputProps={{
                            style: { padding: '1rem' },
                            type: 'number',
                          }}
                          label={active_lang.MAX_FOOD_QUANTITY}
                          variant="outlined"
                          fullWidth
                          value={formik.values.max_food_quantity}
                          onChange={e =>
                            formik.setFieldValue(
                              'max_food_quantity',
                              Number(e.target.value)
                            )
                          }
                        />
                      )}
                    </Box>
                    <Box
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: '8px',
                        alignItems: 'center',
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            id="enable_drink_quantity_control"
                            checked={
                              isUndefined(formik.values) ||
                              isUndefined(
                                formik.values.enable_drink_quantity_control
                              )
                                ? false
                                : formik.values.enable_drink_quantity_control
                            }
                            {...formik.getFieldProps(
                              'enable_drink_quantity_control'
                            )}
                            inputProps={{
                              'aria-label': active_lang.DRINK,
                            }}
                          />
                        }
                        label={active_lang.DRINK}
                      />
                      {formik.values.enable_drink_quantity_control && (
                        <TextField
                          id="max_drink_quantity"
                          {...formik.getFieldProps('max_drink_quantity')}
                          className={classes.TextInput}
                          InputProps={{ disableUnderline: true }}
                          inputProps={{
                            style: { padding: '1rem' },
                            type: 'number',
                          }}
                          label={active_lang.MAX_DRINK_QUANTITY}
                          variant="outlined"
                          fullWidth
                          value={formik.values.max_drink_quantity}
                          onChange={e =>
                            formik.setFieldValue(
                              'max_drink_quantity',
                              Number(e.target.value)
                            )
                          }
                        />
                      )}
                    </Box>
                  </FormGroup>
                </FormControl>
              </Box>
            </Grid>

            <Box mt={'1rem'}>
              <Autocomplete
                id="language"
                {...formik.getFieldProps('language')}
                options={language_options}
                value={getLanguageOptionById(formik.values.language)}
                getOptionLabel={option => option.name}
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Language select"
                    variant="outlined"
                  />
                )}
                onChange={(event, newValue) => {
                  formik.setFieldValue('language', newValue.id);
                }}
                disableClearable
              />
            </Box>

            <Grid item xs={12}>
              <Box mt="1rem">
                <FormControl
                  component="fieldset"
                  style={{
                    width: '100%',
                  }}
                >
                  <FormGroup
                    style={{
                      display: 'flex',
                      gap: '8px',
                      width: '100%',
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          id="enable_order_reminder"
                          checked={
                            isUndefined(formik.values) ||
                            isUndefined(formik.values.enable_order_reminder)
                              ? false
                              : formik.values.enable_order_reminder
                          }
                          {...formik.getFieldProps('enable_order_reminder')}
                          inputProps={{
                            'aria-label': active_lang.ENABLED_ORDER_REMINDER,
                          }}
                        />
                      }
                      label={active_lang.ENABLED_ORDER_REMINDER}
                    />
                    <Box
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                        alignItems: 'stretch',
                      }}
                    >
                      {formik.values.enable_order_reminder && (
                        <TextField
                          id="order_reminder_en"
                          {...formik.getFieldProps('order_reminder_en')}
                          className={classes.TextInput}
                          InputProps={{ disableUnderline: true }}
                          inputProps={{
                            style: { padding: '1rem' },
                          }}
                          label={active_lang.ORDER_REMINDER_EN}
                          variant="outlined"
                          fullWidth
                          multiline
                          minRows={3}
                          style={{ marginBottom: '1rem' }}
                        />
                      )}

                      {formik.values.enable_order_reminder && (
                        <TextField
                          id="order_reminder_ja"
                          {...formik.getFieldProps('order_reminder_ja')}
                          className={classes.TextInput}
                          InputProps={{ disableUnderline: true }}
                          inputProps={{
                            style: { padding: '1rem' },
                          }}
                          label={active_lang.ORDER_REMINDER_JA}
                          variant="outlined"
                          fullWidth
                          multiline
                          minRows={3}
                          style={{ marginBottom: '1rem' }}
                        />
                      )}
                    </Box>
                  </FormGroup>
                </FormControl>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Box mt="1rem" p={2} component={Paper}>
                <Box
                  style={{ display: 'flex', gap: '8px', alignItems: 'center' }}
                >
                  <Typography variant="h6">Pickup Zone</Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={addOption}
                  >
                    Add
                  </Button>
                </Box>

                <Box mt={2}>
                  {options.map(option => (
                    <Box
                      key={option.id}
                      display="flex"
                      alignItems="center"
                      mb={1}
                    >
                      <TextField
                        value={`${option.zoneName} - ${option.mapValue}`}
                        variant="outlined"
                        size="small"
                        disabled
                      />
                      <IconButton
                        onClick={() => handleOpenModal('edit', option.id)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton onClick={() => deleteOption(option.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* Popup Dialog */}
              <Dialog open={openModal} onClose={handleCloseModal}>
                <DialogTitle>
                  {modalType === 'edit'
                    ? 'Edit Zone'
                    : modalType === 'add'
                    ? 'Add Zone'
                    : 'Confirm Delete Zone'}
                </DialogTitle>
                <DialogContent>
                  {modalType === 'edit' || modalType === 'add' ? (
                    <>
                      <TextField
                        autoFocus
                        margin="dense"
                        label="Zone Name"
                        fullWidth
                        value={editZoneName}
                        placeholder="Enter zone name"
                        onChange={e => setEditZoneName(e.target.value)}
                      />
                      <TextField
                        margin="dense"
                        label="Map Value"
                        fullWidth
                        value={editMapValue}
                        placeholder="Enter map value"
                        onChange={e => setEditMapValue(e.target.value)}
                      />
                    </>
                  ) : (
                    <DialogContentText>
                      Are you sure you want to delete this zone?
                    </DialogContentText>
                  )}
                </DialogContent>
                <DialogActions>
                  <Button onClick={handleCloseModal} color="secondary">
                    Cancel
                  </Button>
                  <Button
                    onClick={
                      modalType === 'edit'
                        ? confirmEditOption
                        : modalType === 'add'
                        ? confirmAddOption
                        : confirmDeleteOption
                    }
                    color="primary"
                  >
                    Confirm
                  </Button>
                </DialogActions>
              </Dialog>
            </Grid>

            {formik.values.isStadiumPromotion && (
              <Box mt="1rem">
                <FormControl fullWidth>
                  <Autocomplete
                    id="promotion_restaurant_id"
                    {...formik.getFieldProps('promotion_restaurant_id')}
                    options={restaurants}
                    value={
                      restaurants.find(
                        stadium =>
                          stadium.id === formik.values.promotion_restaurant_id
                      ) || null
                    }
                    getOptionLabel={option => option.name}
                    renderInput={params => (
                      <TextField
                        {...params}
                        label="PromotionRestaurantSelect"
                        variant="outlined"
                      />
                    )}
                    disableClearable
                    onChange={(event, newValue) => {
                      formik.setFieldValue(
                        'promotion_restaurant_id',
                        newValue.id
                      );
                    }}
                  />
                </FormControl>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                formik.resetForm();
                handleClose();
              }}
              type="button"
              color="primary"
            >
              {active_lang.AD_EDIT_CANCEL}
            </Button>
            <Button
              disabled={
                formik.isSubmitting ||
                !formik.dirty ||
                !isEqual(formik.errors, {})
              }
              type={'submit'}
              color="primary"
              variant="contained"
            >
              {active_lang.AD_EDIT_CONFIRM}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
}
